import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { errorHandler } from "@/lib/utils";
import { toast } from "sonner";
import { ActiveFilterBadges } from "./ActiveFilterBadges";
import ColumnFilter from "./ColumnFilter";
import type {
  AdvancedFilterConfig,
  AdvancedFilterDefinition,
  AdvancedFilterOption,
  AdvancedFilterValue,
} from "./data-table-advanced-filter.types";
import {
  buildAdvancedFilterDefinitions,
  isActiveAdvancedFilterValue,
  mapAdvancedFiltersToApiParams,
} from "./data-table-filter-params";
import { DateFilter } from "./DateFilter";

export interface Column<T> {
  key: keyof T | "actions" | "";
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  visible?: boolean;
  render?: (
    row: T,
    handlers: {
      onDelete?: (id: string | number) => void;
      onUpdate?: (id: string | number, data: Partial<T>) => void;
    },
  ) => React.ReactNode;
  filter?: {
    type: "text" | "select" | "date";
    filterType?: string;
    /**
     * Only applies to `type: "date"`. Defaults to "gte_lte", which filters on the
     * raw column value (end date gets a 23:59:59 suffix to include the whole day).
     * Use "date_gte_lte" for columns where the backend should compare only the
     * date part (via `date_gte`/`date_lte`), which is more correct but requires
     * the model's filter class to support those keys.
     */
    dateFilterMode?: "gte_lte" | "date_gte_lte";
    options?: {
      value: string;
      key: string;
    }[];
    label?: string;
    loadOptions?: () => Promise<{ key: string; value: string }[]>;
    default?: string;
    /**
     * Opt-in multi-select. Selected keys are kept comma-joined in the filter
     * state and sent as `key[0]=a&key[1]=b` (a top-level array param, no `eq[]`
     * prefix), for backend filters that take an array.
     */
    multiple?: boolean;
  };
  /**
   * Opt-in advanced filter, reachable only via the "+ Filtro" picker — separate
   * from `filter` (which renders inline in the toolbar). A column can have
   * either, both, or neither.
   */
  advancedFilter?: AdvancedFilterConfig;
}

interface DataTableProps<T> {
  fetchData: (params: {
    page: number;
    per_page: number;
    search?: string;
    filters?: Record<string, string>;
  }) => Promise<{ data: T[]; meta: { total: number } }>;
  columns: Column<T>[];
  pageSize?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  filterable?: boolean;
  onDelete?: (id: string | number) => Promise<boolean>;
  onUpdate?: (id: string | number, data: Partial<T>) => Promise<T>;
  /**
   * Called whenever any filter changes. `apiParams` is the same already-formatted
   * param map the table sends to `fetchData` — prefer it over re-deriving params
   * from the raw state, which is only exposed for UI purposes.
   */
  onFiltersChange?: (
    filters: Record<string, string>,
    dateFilters: Record<string, { startDate?: Date; endDate?: Date }>,
    search: string,
    advancedFilters: Record<string, AdvancedFilterValue>,
    apiParams: Record<string, string>,
  ) => void;
  /**
   * Opt-in row selection: the checkbox column only renders when
   * `onSelectionChange` is set. The table keeps no selection of its own — the
   * page owns it, so it can outlive pagination.
   */
  selectedIds?: ReadonlySet<string | number>;
  /** Called with the rows that were (un)checked, from the current page only. */
  onSelectionChange?: (rows: T[], selected: boolean) => void;
  isRowSelectable?: (row: T) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataTable = <T extends Record<string, any>>({
  fetchData,
  columns,
  pageSize = 10,
  searchable = true,
  searchPlaceholder = "Buscar...",
  filterable = true,
  onDelete,
  onUpdate,
  onFiltersChange,
  selectedIds,
  onSelectionChange,
  isRowSelectable = () => true,
}: DataTableProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<
    Record<string, { key: string; value: string }[]>
  >({});
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    return columns.reduce(
      (acc, column) => {
        const key = column.key.toString();
        if (column.filter?.default) {
          acc[key] = column.filter.default;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  });

  // Handle delete and update local state
  const handleDelete = useCallback(
    async (id: string | number) => {
      if (!onDelete) return;

      try {
        const success = await onDelete(id);
        if (success) {
          toast.success("Registro excluído com sucesso!");

          setData((prevData) => {
            const newData = prevData.filter((item) => item.id !== id);

            if (newData.length === 0 && currentPage > 1) {
              setCurrentPage((prev) => prev - 1);
            }

            setTotal((prev) => prev - 1);

            return newData;
          });
        }
      } catch (err) {
        errorHandler(err, "Erro ao excluir registro!");
      }
    },
    [onDelete, currentPage],
  );

  // Handle update and update local state
  const handleUpdate = useCallback(
    async (id: string | number, updateData: Partial<T>) => {
      if (!onUpdate) return;

      try {
        const updatedItem = await onUpdate(id, updateData);
        toast.success("Registro atualizado com sucesso!");

        setData((prevData) =>
          prevData.map((item) =>
            item.id === id ? { ...item, ...updatedItem } : item,
          ),
        );
      } catch (err) {
        errorHandler(err, "Erro ao atualizar registro!");
      }
    },
    [onUpdate],
  );

  // Date filters state
  const [dateFilters, setDateFilters] = useState<
    Record<string, { startDate?: Date; endDate?: Date }>
  >({});

  const [advancedFilters, setAdvancedFilters] = useState<
    Record<string, AdvancedFilterValue>
  >({});
  const [advancedFilterOptions, setAdvancedFilterOptions] = useState<
    Record<string, AdvancedFilterOption[]>
  >({});

  /** Filter option loaders already issued, so they run once per mount. */
  const loadedOptionKeysRef = useRef<Set<string>>(new Set());

  const advancedFilterDefinitions: AdvancedFilterDefinition[] = useMemo(
    () => buildAdvancedFilterDefinitions(columns),
    [columns],
  );

  /**
   * Filters the page ships with (e.g. Tickets opens scoped to "Pendente").
   * These define the screen's landing scope, so resetting restores them
   * instead of clearing them.
   */
  const defaultFilters = useMemo(
    () =>
      columns.reduce(
        (acc, column) => {
          const key = column.key.toString();
          if (column.filter?.default) {
            acc[key] = column.filter.default;
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
    [columns],
  );

  /**
   * True when the current state differs from the page's defaults — i.e. when
   * there is something for the reset button to undo. Compares values rather
   * than keys because cleared filters linger as empty strings (`handleFilter`)
   * and cleared date ranges linger as `{ startDate: undefined }`
   * (`handleDateFilter`).
   */
  const hasActiveFilters = useMemo(() => {
    const applied = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== ""),
    );

    const differsFromDefaults =
      Object.keys(applied).length !== Object.keys(defaultFilters).length ||
      Object.entries(defaultFilters).some(
        ([key, value]) => applied[key] !== value,
      );

    const hasDateFilter = Object.values(dateFilters).some(
      (range) => range.startDate || range.endDate,
    );

    return (
      differsFromDefaults ||
      hasDateFilter ||
      Boolean(search) ||
      Object.values(advancedFilters).some(isActiveAdvancedFilterValue)
    );
  }, [filters, defaultFilters, dateFilters, search, advancedFilters]);

  /**
   * The inline, date and advanced filters collapsed into the query-param shape
   * the API expects (`eq[field]`, `gte[field]`, ...). This is the single source
   * of truth for filter formatting: it feeds both the table's own fetch and the
   * `onFiltersChange` callback, so consumers that hit other endpoints with the
   * same filters (PDF/CSV exports) never have to re-derive it.
   */
  const apiFilterParams = useMemo(() => {
    const formattedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (value) {
          const column = columns.find((col) => col.key === key);
          if (column?.filter?.multiple) {
            value.split(",").forEach((part, index) => {
              acc[`${key}[${index}]`] = part;
            });
          } else {
            const filterType = column?.filter?.filterType || "eq";
            acc[`${filterType}[${key}]`] = value;
          }
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    // Add date filters
    Object.entries(dateFilters).forEach(([key, { startDate, endDate }]) => {
      const column = columns.find((col) => col.key === key);
      const useDateOnlyComparison =
        column?.filter?.dateFilterMode === "date_gte_lte";
      const gtePrefix = useDateOnlyComparison ? "date_gte" : "gte";
      const ltePrefix = useDateOnlyComparison ? "date_lte" : "lte";

      if (startDate) {
        formattedFilters[`${gtePrefix}[${key}]`] = format(
          startDate,
          "yyyy-MM-dd",
        );
      }
      if (endDate) {
        formattedFilters[`${ltePrefix}[${key}]`] = useDateOnlyComparison
          ? format(endDate, "yyyy-MM-dd")
          : format(endDate, "yyyy-MM-dd'T'23:59:59");
      }
    });

    Object.assign(
      formattedFilters,
      mapAdvancedFiltersToApiParams(advancedFilters, advancedFilterDefinitions),
    );

    return formattedFilters;
  }, [
    filters,
    dateFilters,
    advancedFilters,
    advancedFilterDefinitions,
    columns,
  ]);

  const fetchDataFromServer = async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        page: currentPage,
        per_page: pageSize,
        search: search || undefined,
        filters: apiFilterParams,
      });

      setData(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      errorHandler(err, "Erro ao carregar dados!");
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = (key: string, startDate?: Date, endDate?: Date) => {
    setDateFilters((prev) => ({
      ...prev,
      [key]: { startDate, endDate },
    }));
    setCurrentPage(1);
  };

  const handleAdvancedFilterApply = (
    key: string,
    value: AdvancedFilterValue,
  ) => {
    setAdvancedFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleAdvancedFilterRemove = (key: string) => {
    setAdvancedFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
  };

  const handleResetAllFilters = () => {
    setFilters(defaultFilters);
    setDateFilters({});
    setAdvancedFilters({});
    setSearch("");
    setCurrentPage(1);
  };

  // Carregar opções de filtro
  useEffect(() => {
    /**
     * Loads a filter's options at most once per mount. Pages build `columns` as
     * a plain array literal, so this effect re-runs on every parent render; the
     * guard is what keeps that from re-issuing every request. Reserving the key
     * before awaiting also collapses concurrent runs, and releasing it on
     * failure lets a later render retry.
     */
    const loadOptionsOnce = async (
      cacheKey: string,
      loadOptions: () => Promise<AdvancedFilterOption[]>,
      apply: (options: AdvancedFilterOption[]) => void,
      errorMessage: string,
    ) => {
      if (loadedOptionKeysRef.current.has(cacheKey)) return;
      loadedOptionKeysRef.current.add(cacheKey);

      try {
        apply(await loadOptions());
      } catch (err) {
        loadedOptionKeysRef.current.delete(cacheKey);
        errorHandler(err, errorMessage);
      }
    };

    const loadFilterOptions = async () => {
      for (const column of columns) {
        const key = column.key as string;

        if (column.filterable && column.filter?.loadOptions) {
          await loadOptionsOnce(
            `filter:${key}`,
            column.filter.loadOptions,
            (options) =>
              setFilterOptions((prev) => ({ ...prev, [key]: options })),
            "Erro ao carregar opções de filtro!",
          );
        }

        if (column.advancedFilter?.loadOptions) {
          await loadOptionsOnce(
            `advanced:${key}`,
            column.advancedFilter.loadOptions,
            (options) =>
              setAdvancedFilterOptions((prev) => ({ ...prev, [key]: options })),
            "Erro ao carregar opções de filtro avançado!",
          );
        }
      }
    };

    loadFilterOptions();
  }, [columns]);

  useEffect(() => {
    fetchDataFromServer();
    // fetchDataFromServer intentionally omitted: search/date filters trigger
    // fetches explicitly (via applyFilters/handleKeyPress), not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, advancedFilters]);

  // Notify parent component of filter changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(
        filters,
        dateFilters,
        search,
        advancedFilters,
        apiFilterParams,
      );
    }
  }, [
    filters,
    dateFilters,
    search,
    advancedFilters,
    apiFilterParams,
    onFiltersChange,
  ]);

  const handleFilter = (key: string, value: string | null) => {
    if (
      value &&
      typeof value === "string" &&
      value.startsWith("group-header-")
    ) {
      return;
    }

    if (value === null) {
      const newFilters = { ...filters };
      delete newFilters[key];
      setFilters(newFilters);
    } else {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      fetchDataFromServer();
    }
  };

  const totalPages = useMemo(
    () => Math.ceil(total / pageSize),
    [total, pageSize],
  );

  const selectable = Boolean(onSelectionChange);
  const selectableRows = selectable ? data.filter(isRowSelectable) : [];
  const allPageRowsSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedIds?.has(row.id));
  const visibleColumnCount =
    columns.filter((column) => column.visible !== false).length +
    (selectable ? 1 : 0);

  const applyFilters = () => {
    setCurrentPage(1);
    fetchDataFromServer();
  };

  return (
    <div className="w-full space-y-4">
      {/* Barra de Pesquisa e Filtros */}
      <div className="space-y-4">
        {/* Date Filters Section */}
        {filterable &&
          columns.some(
            (column) =>
              column.filterable &&
              column.filter &&
              column.filter.type === "date",
          ) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {columns
                .filter(
                  (column) =>
                    column.filterable &&
                    column.filter &&
                    column.filter.type === "date",
                )
                .map((column) => {
                  const columnKey = column.key as string;
                  return (
                    <DateFilter
                      key={columnKey}
                      startDate={dateFilters[columnKey]?.startDate}
                      endDate={dateFilters[columnKey]?.endDate}
                      onStartDateChange={(date) => {
                        handleDateFilter(
                          columnKey,
                          date,
                          dateFilters[columnKey]?.endDate,
                        );
                      }}
                      onEndDateChange={(date) => {
                        handleDateFilter(
                          columnKey,
                          dateFilters[columnKey]?.startDate,
                          date,
                        );
                      }}
                      onClear={() => {
                        handleDateFilter(columnKey, undefined, undefined);
                      }}
                      onFilter={applyFilters}
                      label={
                        (column.filter as NonNullable<typeof column.filter>)
                          .label || column.label
                      }
                    />
                  );
                })}
            </div>
          )}

        {/* Other Filters Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {searchable && (
            <div className="relative col-span-1 md:col-span-2 lg:col-span-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                className="pl-8 text-foreground w-full"
              />
            </div>
          )}

          {filterable &&
            columns
              .filter(
                (column) =>
                  column.filterable &&
                  column.filter &&
                  column.filter.type !== "date",
              )
              .map((column) => (
                <ColumnFilter
                  column={column}
                  filterOptions={filterOptions}
                  filters={filters}
                  loading={loading}
                  onFilter={handleFilter}
                  key={column.key as string}
                />
              ))}
        </div>

        {filterable && advancedFilterDefinitions.length > 0 && (
          <ActiveFilterBadges
            definitions={advancedFilterDefinitions}
            activeFilters={advancedFilters}
            loadedOptions={advancedFilterOptions}
            hasActiveFilters={hasActiveFilters}
            onApply={handleAdvancedFilterApply}
            onRemove={handleAdvancedFilterRemove}
            onReset={handleResetAllFilters}
          />
        )}
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                {selectable && (
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      aria-label="Selecionar todos da página"
                      data-testid="datatable-select-page"
                      checked={allPageRowsSelected}
                      disabled={loading || selectableRows.length === 0}
                      onCheckedChange={(checked) =>
                        onSelectionChange?.(selectableRows, checked === true)
                      }
                    />
                  </th>
                )}
                {columns
                  .filter((column) => column.visible !== false)
                  .map((column) => (
                    <th
                      key={column.key as string}
                      className="px-4 py-3 text-left font-medium text-muted-foreground"
                    >
                      {column.label}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-background">
              {loading ? (
                <tr>
                  <td
                    colSpan={visibleColumnCount}
                    className="px-4 py-3 text-center text-muted-foreground"
                  >
                    Carregando...
                  </td>
                </tr>
              ) : data.length ? (
                data.map((row, index) => (
                  <tr
                    key={index}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    {selectable && (
                      <td className="w-10 px-4 py-3">
                        {isRowSelectable(row) && (
                          <Checkbox
                            aria-label="Selecionar linha"
                            data-testid={`datatable-select-row-${row.id}`}
                            checked={selectedIds?.has(row.id) ?? false}
                            onCheckedChange={(checked) =>
                              onSelectionChange?.([row], checked === true)
                            }
                          />
                        )}
                      </td>
                    )}
                    {columns
                      .filter((column) => column.visible !== false)
                      .map((column) => (
                        <td
                          key={column.key as string}
                          className="px-4 py-3 text-foreground"
                        >
                          {column.render
                            ? column.render(row, {
                                onDelete: handleDelete,
                                onUpdate: handleUpdate,
                              })
                            : row[column.key]}
                        </td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={visibleColumnCount}
                    className="px-4 py-3 text-center text-muted-foreground"
                  >
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Mostrando {(currentPage - 1) * pageSize + 1} até{" "}
          {Math.min(currentPage * pageSize, total)} de {total} registros
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="text-muted-foreground hover:text-foreground border-border hover:bg-accent/50 hover:border-accent disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="text-muted-foreground hover:text-foreground border-border hover:bg-accent/50 hover:border-accent disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
            className="text-muted-foreground hover:text-foreground border-border hover:bg-accent/50 hover:border-accent disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="text-muted-foreground hover:text-foreground border-border hover:bg-accent/50 hover:border-accent disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
